import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import { convertAudioBlobToMp3 } from "../../utils/audioToMp3";

export default function VoiceSendModal({
    open,
    onClose,
    userId,
    onSave,
    isProfileNote = false,
    contentPanelRef = null,
}) {
    const token = Cookies.get("auth_token");
    const modalRef = useRef();

    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const discardRecordingRef = useRef(false);
    const segmentChunksRef = useRef([]);
    const [recording, setRecording] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [seconds, setSeconds] = useState(0);
    const [panelBounds, setPanelBounds] = useState(null);

    // Chunks since last send/reset (segment buffer)
    const [segmentChunks, setSegmentChunks] = useState([]);

    const usePanelOverlay = Boolean(isProfileNote && contentPanelRef);

    // Timer while recording
    useEffect(() => {
        let interval;
        if (recording) {
            interval = setInterval(() => setSeconds((s) => s + 1), 1000);
        }
        return () => clearInterval(interval);
    }, [recording]);

    const time = new Date(seconds * 1000).toISOString().substring(14, 19);

    useLayoutEffect(() => {
        if (!open || !usePanelOverlay || !contentPanelRef?.current) {
            setPanelBounds(null);
            return undefined;
        }

        const updateBounds = () => {
            const rect = contentPanelRef.current.getBoundingClientRect();
            setPanelBounds({
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
            });
        };

        updateBounds();

        const observer = new ResizeObserver(updateBounds);
        observer.observe(contentPanelRef.current);

        window.addEventListener("resize", updateBounds);
        window.addEventListener("scroll", updateBounds, true);

        return () => {
            observer.disconnect();
            window.removeEventListener("resize", updateBounds);
            window.removeEventListener("scroll", updateBounds, true);
        };
    }, [open, usePanelOverlay, contentPanelRef]);

    // Cleanup on unmount: stop recording and tracks
    useEffect(() => {
        return () => {
            try {
                if (recorderRef.current && recorderRef.current.state !== "inactive") {
                    recorderRef.current.stop();
                }
            } catch {}
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
            }
        };
    }, []);

    const handleStart = async () => {
        try {
            // If recorder exists and is paused, resume instead of creating a new one
            const existing = recorderRef.current;
            if (existing && existing.state === "paused") {
                try {
                    existing.resume();
                } catch (err) {
                    }
                return;
            }

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
                ? "audio/webm;codecs=opus"
                : "audio/webm";

            const recorder = new MediaRecorder(stream, { mimeType });
            recorderRef.current = recorder;
            setErrorMsg("");
            setSeconds(0);
            // Starting fresh recording - clear previous segments
            segmentChunksRef.current = [];
            setSegmentChunks([]);

            recorder.ondataavailable = (event) => {
                if (!discardRecordingRef.current && event.data && event.data.size > 0) {
                    segmentChunksRef.current = [...segmentChunksRef.current, event.data];
                    setSegmentChunks(segmentChunksRef.current);
                }
            };

            recorder.onstart = () => setRecording(true);
            recorder.onstop = () => {
                setRecording(false);
                // When recorder is stopped (finalized), clear the ref so a new
                // recording will create a clean MediaRecorder instance.
                try { recorderRef.current = null; } catch {}
            };
            recorder.onpause = () => setRecording(false);
            recorder.onresume = () => setRecording(true);

            discardRecordingRef.current = false;
            // Start emitting chunks every 500ms
            recorder.start(500);
        } catch (e) {
            setErrorMsg("Microphone access denied.");
            toast.error("Microphone access denied.");
        }
    };

    useEffect(() => {
        if (!open || recording || recorderRef.current) return;

        const timer = setTimeout(() => {
            handleStart();
        }, 300);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handlePause = () => {
        try {
            const rec = recorderRef.current;
            if (rec && rec.state === "recording") {
                rec.pause();
            }
        } catch (err) {
            }
    };

    const handleStop = (discard = false) => {
        if (discard) {
            discardRecordingRef.current = true;
        }

        try {
            if (recorderRef.current && recorderRef.current.state !== "inactive") {
                recorderRef.current.stop();
            }
        } catch (e) {
            }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
    };

    const handleClose = () => {
        handleStop(true);
        segmentChunksRef.current = [];
        setSegmentChunks([]);
        setSeconds(0);
        setErrorMsg("");
        onClose();
    };

    const handleDelete = () => {
        handleStop(true);
        segmentChunksRef.current = [];
        setSegmentChunks([]);
        setSeconds(0);
        setErrorMsg("");
        toast.dismiss();
        toast("Recording deleted successfully.");
        onClose();
    };

    const buildSegmentBlob = () => {
        if (!segmentChunksRef.current || segmentChunksRef.current.length === 0) return null;
        return new Blob(segmentChunksRef.current, { type: "audio/webm" });
    };

    const flushRecordingData = async () => {
        const recorder = recorderRef.current;
        if (!recorder || recorder.state !== "recording") return;

        try {
            recorder.requestData();
        } catch {}

        await new Promise((resolve) => setTimeout(resolve, 100));
    };

    const handleSend = async () => {
        await flushRecordingData();
        const blob = buildSegmentBlob();
        if (!blob || blob.size === 0) {
            toast.error("Nothing to send yet. Please speak a bit more.");
            return;
        }

        try {
            const mp3Blob = await convertAudioBlobToMp3(blob);
            const formData = new FormData();
            formData.append("note", "");
            formData.append("voice_note", mp3Blob, "recorded.mp3");

            const endpoint = isProfileNote ? "/profile-note/set" : "/friend-personal-note/set";

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });

            const data = await res.json();

            if (data.status === "1") {
                toast.success("Voice note added.");
                // After sending, reset the segment buffer but keep recording going
                segmentChunksRef.current = [];
                setSegmentChunks([]);
                if (!recording) {
                    // If not recording anymore, also close
                    onClose();
                }
                if (onSave) onSave();
            } else {
                toast.error(data?.message || "Failed to save voice note.");
            }
        } catch (err) {
            toast.error("Error sending voice note.");
        }
    };

    if (!open) return null;
    if (usePanelOverlay && !panelBounds) return null;

    const modalContent = (
        <div ref={modalRef} className="relative bg-white rounded-3xl px-8 py-6 shadow-xl w-80">
            <button
                type="button"
                onClick={handleClose}
                className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 transition hover:bg-gray-200"
                aria-label="Close"
            >
                <img src="../chatbox/close.svg" alt="close" className="h-4 w-4" />
            </button>
            <div className="flex justify-center mb-4">
                <button onClick={() => (recording ? handlePause() : handleStart())}>
                    <img
                        src={recording ? "../chatscreen/pause.svg" : "../chatscreen/main-mic.svg"}
                        className={`w-28 h-28 ${recording ? "animate-pulse" : ""}`}
                        alt={recording ? "pause" : "mic"}
                    />
                </button>
            </div>

            <div className="text-center text-xl font-semibold mb-3">{time}</div>

            <div className="flex justify-center gap-8 mt-4">
                <button type="button" onClick={handleDelete}>
                    <img src="../chatscreen/trash.svg" className="w-10 h-6" alt="delete" />
                </button>

                <button
                    type="button"
                    onClick={handleSend}
                    disabled={segmentChunks.length === 0}
                    className="disabled:opacity-40"
                >
                    <img src="../chatscreen/send-arrow.svg" className="w-10 h-10" alt="send" />
                </button>
            </div>

            {errorMsg && (
                <p className="text-red-500 text-xs mt-3 text-center">{errorMsg}</p>
            )}
        </div>
    );

    if (usePanelOverlay) {
        return createPortal(
            <div
                className="fixed z-50 flex items-center justify-center backdrop-blur-sm"
                style={{
                    top: 0,
                    left: panelBounds.left,
                    width: panelBounds.width + 24,
                    height: panelBounds.height + 48,
                }}
            >
                {modalContent}
            </div>,
            document.body
        );
    }

    return (
        <div className="absolute inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
            {modalContent}
        </div>
    );
}

